import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { Box } from "@mui/material";
import { createStyles, spacing } from "../../styles/theme";
import { useSpecsEditor, useSpecsEditorContextValue } from "./useSpecsEditor";
import { APP_SHELL_ACTION_ENUM } from "../../common/reducers/appShell";
import SpecsToolbar from "./SpecsToolbar";
import SpecsEditorPageRouter from "./SpecsEditorPageRouter";
import SpecsEditorLeftSidebar from "./SpecsEditorLeftSidebar";
import SpecsEditorRightSidebar from "./SpecsEditorRightSidebar";
import SpecsEditorLayers from "./SpecsEditorLayers";
import { SpecsEditorContext } from "./useSpecsEditor";
import { SpecsModelContext } from "./SpecsModelContext";
import SpecsEditorModals, { useSpecsEditorModals } from "./SpecsEditorModals";
import SpecsValidationIssueSummary from "./SpecsValidationIssueSummary";
import { useSpecsEditorHashNavigation } from "./useSpecsEditorHashNavigation";
import { useSpecsRevisionHistory } from "./useSpecsRevisionHistory";

// ── Styles ──────────────────────────────────────────────────────────

const styles = createStyles({
  root: {
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    p: { xs: spacing.wrapperMobile, lg: "18px" },
    maxHeight: "100vh",
    overflowY: "auto",
  },
});

// ── Main Component ──────────────────────────────────────────────────

const SpecsEditor = () => {
  const dispatch = useDispatch();
  const { productName: urlProduct, version: urlVersion } = useParams<{
    productName?: string;
    version?: string;
  }>();

  // Derive session key from URL params for per-product storage
  const sessionKey = urlProduct
    ? `${urlProduct}:${urlVersion || "1.0"}`
    : undefined;

  useEffect(() => {
    dispatch({
      type: APP_SHELL_ACTION_ENUM.SET_SHOW_LEFT_NAVIGATION,
      payload: { showLeftNavigation: false },
      error: null,
    });

    return () => {
      dispatch({
        type: APP_SHELL_ACTION_ENUM.SET_SHOW_LEFT_NAVIGATION,
        payload: { showLeftNavigation: true },
        error: null,
      });
    };
  }, [dispatch]);

  const editor = useSpecsEditor({
    sessionKey,
    initialImportProduct: urlProduct || "",
    initialImportVersion: urlVersion || "",
  });
  const modals = useSpecsEditorModals();

  useSpecsEditorHashNavigation({ editor });

  const revisionHistory = useSpecsRevisionHistory({
    editor,
    modals,
  });

  useEffect(() => {
    modals.showValidationResult({ valid: true, issues: [] });
  }, [
    editor.identity.productName,
    editor.identity.productVersion,
    modals.showValidationResult,
  ]);

  const specsEditorContextValue = useSpecsEditorContextValue({
    editor,
    revisionHistory,
    modals,
  });

  return (
    <SpecsModelContext.Provider
      value={{
        specsModel: editor.specsModel,
        setSpecsModel: editor.setSpecsModel,
      }}
    >
      <SpecsEditorContext.Provider value={specsEditorContextValue}>
        <Box
          sx={{
            display: "flex",
            height: "100vh",
            overflow: "hidden",
            width: "100%",
          }}
        >
          <SpecsEditorLeftSidebar />

          {/* ── Left column (top bar + main content) ──────────── */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              minWidth: 0,
            }}
          >
            {/* ── Top Bar ─────────────────────────────────────── */}
            <SpecsToolbar />

            {/* ── Main content ─────────────────────────────────────── */}
            <Box sx={styles.root}>
              <SpecsValidationIssueSummary
                validationResult={modals.validationResult}
                onOpenDetails={modals.openValidationDialog}
              />
              <SpecsEditorPageRouter />

              {/* ── Eager image processors for all visible layers ──── */}
              <SpecsEditorLayers />
            </Box>
          </Box>
          {/* end flex column */}

          <SpecsEditorRightSidebar />
          <SpecsEditorModals />
        </Box>
      </SpecsEditorContext.Provider>
    </SpecsModelContext.Provider>
  );
};

export default SpecsEditor;
